/**
 * PasswordInput — text input with reveal/hide toggle.
 *
 * Drop-in replacement for <input type="password" /> with an eye icon toggle.
 *
 * CALLERS MUST PASS `autoComplete`. There is no safe default: a sign-in field
 * wants `current-password`, a change/reset/sign-up field wants `new-password`,
 * and a secret that is not the user's account password (API keys, DB
 * credentials) wants `off`. Leaving it unset makes the browser guess, and it
 * guesses "login form" — which autofills the saved account password into a
 * "New password" box.
 */
import { useState, forwardRef, type InputHTMLAttributes } from 'react'
import { Eye, EyeOff } from 'lucide-react'

interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Additional classes for the wrapper div */
  wrapperClassName?: string
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ wrapperClassName, className, ...props }, ref) {
    const [revealed, setRevealed] = useState(false)

    return (
      <div className={`relative ${wrapperClassName || ''}`}>
        <input
          {...props}
          ref={ref}
          type={revealed ? 'text' : 'password'}
          className={`${className || ''} pr-9`}
        />
        <button
          type="button"
          onClick={() => setRevealed(r => !r)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 themed-text-muted hover:themed-text transition-colors"
          tabIndex={-1}
        >
          {revealed
            ? <EyeOff size={16} />
            : <Eye size={16} />
          }
        </button>
      </div>
    )
  }
)
